import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { Transcript } from 'api/types/transcript';
import SessionsApi from 'api/sessions';

interface TranscriptState {
  transcripts: Transcript[];
  loading: boolean;
  error?: string;
}

const initialState: TranscriptState = {
  transcripts: [],
  loading: false,
  error: undefined
};

export const fetchTranscriptsBySessionId = createAsyncThunk<
  {
    transcripts: Transcript[];
    isInitialLoad: boolean;
    totalRecords: number;
    isPolling?: boolean;
    sessionType?: string;
  },
  {
    sessionId: string;
    page: number;
    limit: number;
    isInitialLoad?: boolean;
    isPolling?: boolean;
    sessionType?: string;
  },
  { rejectValue: string }
>(
  'transcript/fetchTranscriptsBySessionId',
  async (
    { sessionId, page, limit, isInitialLoad = false, isPolling = false, sessionType },
    { rejectWithValue }
  ) => {
    try {
      const response = await SessionsApi.fetchTranscriptsBySessionId(sessionId, limit, page);
      if (!response.data) {
        throw new Error('No data found');
      }
      if (response.status !== 200) {
        throw new Error(`Error: ${response.status}`);
      }
      return {
        transcripts: response.data.data,
        isInitialLoad,
        totalRecords: response.data.totalRecords || 0,
        isPolling,
        sessionType
      };
    } catch (err: unknown) {
      let errorMessage = 'Failed to fetch transcripts';
      if (err instanceof Error) {
        errorMessage = err.message;
      }
      return rejectWithValue(errorMessage);
    }
  }
);

const transcriptSlice = createSlice({
  name: 'transcript',
  initialState,
  reducers: {
    clearTranscripts: (state) => {
      state.transcripts = [];
    },
    addTranscript: (state, action: PayloadAction<Transcript>) => {
      const newTranscript = action.payload;
      const existingIndex = state.transcripts.findIndex((t) => t.id === newTranscript.id);
      if (existingIndex === -1) {
        const insertIndex = state.transcripts.findIndex(
          (t) => new Date(t.startDate).getTime() > new Date(newTranscript.startDate).getTime()
        );

        if (insertIndex === -1) {
          state.transcripts.push(newTranscript);
        } else {
          state.transcripts.splice(insertIndex, 0, newTranscript);
        }
      }
    },
    removeTranscript: (state, action: PayloadAction<string>) => {
      const transcriptId = action.payload;
      state.transcripts = state.transcripts.filter((t) => t.id !== transcriptId);
    },
    updateTranscript: (state, action: PayloadAction<{ id: string; content: string }>) => {
      const { id, content } = action.payload;
      const transcript = state.transcripts.find((t) => t.id === id);
      if (transcript) {
        transcript.content = content;
      }
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTranscriptsBySessionId.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(
        fetchTranscriptsBySessionId.fulfilled,
        (
          state,
          action: PayloadAction<{
            transcripts: Transcript[];
            isInitialLoad: boolean;
            totalRecords: number;
            isPolling?: boolean;
            sessionType?: string;
          }>
        ) => {
          state.loading = false;
          const { transcripts, isInitialLoad, isPolling = false } = action.payload;

          if (isInitialLoad) {
            state.transcripts = transcripts.reverse();
          } else if (isPolling) {
            const existingIds = new Set(state.transcripts.map((t) => t.id));
            const newTranscripts = transcripts.filter((t) => !existingIds.has(t.id));

            if (newTranscripts.length > 0) {
              const sortedNewTranscripts = newTranscripts.sort(
                (a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime()
              );
              state.transcripts = [...state.transcripts, ...sortedNewTranscripts];
            }
          } else {
            const existingIds = new Set(state.transcripts.map((t) => t.id));
            const newTranscripts = transcripts.filter((t) => !existingIds.has(t.id));

            if (newTranscripts.length > 0) {
              state.transcripts = [...newTranscripts.reverse(), ...state.transcripts];
            }
          }
        }
      )
      .addCase(fetchTranscriptsBySessionId.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? 'Something went wrong';
      });
  }
});

export const { clearTranscripts, addTranscript, removeTranscript, updateTranscript } =
  transcriptSlice.actions;
export default transcriptSlice.reducer;
